import { Injectable } from '@angular/core';
import { ApiConfig } from '../../../environments/environment.model';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root',
})
export class ApiConfigService {
  constructor(private readonly configService: ConfigService) {}

  private get config(): ApiConfig {
    return this.configService.api;
  }

  get urlBase(): string {
    return this.config.urlBase;
  }

  get uriSuffixe(): string {
    return this.config.uriSuffixe;
  }

  /** /api-produit{suffixe}/product-type/findall */
  get productListUrl(): string {
    return this.buildUrl('PRODUIT', 'PRODUCT-TYPE', 'FIND_ALL');
  }

  /** /api-design{suffixe}/agency/findall */
  get agencyListUrl(): string {
    return this.buildUrl('DESIGN', 'AGENCY', 'FIND_ALL');
  }

  /** GET /api-design{suffixe}/info/findall */
  get infoListUrl(): string {
    return this.buildUrl('DESIGN', 'INFO', 'FIND_ALL');
  }

  /** /api-design{suffixe}/color/findall */
  get colorListUrl(): string {
    return this.buildUrl('DESIGN', 'COLOR', 'FIND_ALL');
  }

  /** POST /api-design{suffixe}/notification/find/by/user/deleted/page */
  get notificationsPageUrl(): string {
    return this.buildUrl('DESIGN', 'NOTIFICATION', 'PAGINATED_NOTIFICATION');
  }

  /** PATCH /api-design{suffixe}/notification/change-status */
  get notificationsMarkReadUrl(): string {
    return this.buildUrl('DESIGN', 'NOTIFICATION', 'MASS_UPDATE');
  }

  /** DELETE /api-design{suffixe}/notification/delete/{id} */
  get notificationsDeleteBaseUrl(): string {
    return `${this.buildUrl('DESIGN', 'NOTIFICATION', 'DELETE')}/`;
  }

  /** WebSocket wss://{api.urlBase host}/api-design{suffixe}/mc?user={userId} */
  buildNotificationWebSocketUrl(userId: string): string {
    const path = `${this.config.microservice.DESIGN}${this.config.uriSuffixe}${this.config.subResource.SOCKET}`;

    if (this.config.urlBase) {
      const baseUrl = new URL(this.config.urlBase);
      const wsProtocol =
        this.config.wsBaseUrl === 'wss' || baseUrl.protocol === 'https:'
          ? 'wss:'
          : 'ws:';
      const basePath = baseUrl.pathname.replace(/\/$/, '');
      return `${wsProtocol}//${baseUrl.host}${basePath}${path}?user=${encodeURIComponent(userId)}`;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}${path}?user=${encodeURIComponent(userId)}`;
  }

  /** /api-produit{suffixe}/image/findall */
  get imageListUrl(): string {
    return this.buildUrl('PRODUIT', 'IMAGE', 'FIND_ALL');
  }

  /** /api-souscription{suffixe}/souscription/v1/devis */
  get devisUrl(): string {
    return this.buildUrl('SOUSCRIPTION', 'SOUSCRIPTION', 'DEVIS');
  }

  /** POST /api-souscription{suffixe}/souscription/v1/afn */
  get afnUrl(): string {
    return this.buildUrl('SOUSCRIPTION', 'SOUSCRIPTION', 'AFN');
  }

  /** POST /api-souscription{suffixe}/souscription/v1/afnv */
  get afnvUrl(): string {
    return this.buildUrl('SOUSCRIPTION', 'SOUSCRIPTION', 'AFNV');
  }

  /** POST /api-souscription{suffixe}/souscription/v1/initdata */
  get initDataUrl(): string {
    return this.buildUrl('SOUSCRIPTION', 'SOUSCRIPTION', 'INITDATA');
  }

  /** POST /api-souscription{suffixe}/souscription/v1/print/{idenetat}/{codeinte}/{numepoli} */
  buildPrintUrl(idenetat: string, codeinte: string, numepoli: string): string {
    const base = this.buildUrl('SOUSCRIPTION', 'SOUSCRIPTION', 'PRINT');
    return `${base}/${encodeURIComponent(idenetat)}/${encodeURIComponent(codeinte)}/${encodeURIComponent(numepoli)}`;
  }

  /** GET /api-produit{suffixe}/policy/v2/find?phoneNumber= */
  get policyListUrl(): string {
    return this.buildUrl('PRODUIT', 'POLICY', 'POLICY');
  }

  /** POST /api-produit{suffixe}/policy/v1/find-policy-detail */
  get policyDetailUrl(): string {
    return this.buildUrl('PRODUIT', 'POLICY', 'POLICY_DETAIL');
  }

  /** POST /api-souscription{suffixe}/policy/v1/encaissement/versement */
  get policyFreePaymentUrl(): string {
    return this.buildUrl('SOUSCRIPTION', 'POLICY', 'FREE_PAYMENT');
  }

  /** POST /api-souscription{suffixe}/policy/v1/encaissement/terme */
  get policyPeriodicPaymentUrl(): string {
    return this.buildUrl('SOUSCRIPTION', 'POLICY', 'PERIODIC_PAYMENT');
  }

  /** POST /api-souscription{suffixe}/policy/v1/life/consult */
  get policyLifeConsultUrl(): string {
    return this.buildUrl('SOUSCRIPTION', 'POLICY', 'CONSULT_POLICY');
  }

  /** GET /api-design{suffixe}/sinistre/v1/print/{userId}/{policy}/{reference} */
  buildSinisterPrintUrl(
    userId: string,
    policy: string,
    reference: string,
  ): string {
    const base = this.buildUrl('DESIGN', 'SINISTER', 'PRINT');
    return `${base}/${encodeURIComponent(userId)}/${encodeURIComponent(policy)}/${encodeURIComponent(reference)}`;
  }

  /** GET /api-produit{suffixe}/project/v2/contractless/find?phoneNumber= */
  get contractlessFindUrl(): string {
    return this.buildUrl('PRODUIT', 'PROJECT', 'GET_CONTRACTLESS_PROJECT');
  }

  /** DELETE /api-souscription{suffixe}/project/v1/delete */
  get contractlessDeleteUrl(): string {
    return this.buildUrl(
      'SOUSCRIPTION',
      'PROJECT',
      'DELETE_CONTRACTLESS_PROJECT',
    );
  }

  /** GET /api-design{suffixe}/sinistre/findby/user */
  get sinisterByUserUrl(): string {
    return this.buildUrl('DESIGN', 'SINISTER', 'FIND_BY_USER');
  }

  /** POST /api-design{suffixe}/sinistre/create */
  get sinisterCreateUrl(): string {
    return this.buildUrl('DESIGN', 'SINISTER', 'CREATE');
  }
  /** GET /api-payment{suffixe}/provider/findall */
  get paymentProvidersUrl(): string {
    return this.buildUrl('PAYMENT', 'PROVIDER', 'FIND_ALL');
  }

  /** POST /api-payment{suffixe}/payment/pay */
  get paymentPayUrl(): string {
    return this.buildUrl('PAYMENT', 'PAYMENT', 'PAY');
  }

  /** GET /api-payment{suffixe}/transaction/find/by/issuer/paged/{userId} */
  get transactionsByIssuerPagedUrl(): string {
    return this.buildUrl(
      'PAYMENT',
      'TRANSACTION',
      'GET_TRANSACTION_BY_ISSUER_PAGED',
    );
  }

  buildUrl(
    microservice: keyof ApiConfig['microservice'],
    subResource: keyof ApiConfig['subResource'],
    uri?: keyof ApiConfig['uri'],
  ): string {
    const base = `${this.config.urlBase}${this.config.microservice[microservice]}${this.config.uriSuffixe}${this.config.subResource[subResource]}`;
    return uri ? `${base}${this.config.uri[uri]}` : base;
  }
}
